import React, { useEffect, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { DeviceType, useDeviceType } from '@library/dom'

import { CustomisationEducation } from './CustomisationEducation'
import { RecipeBanner } from './RecipeBanner'
import { RecipeTileBody } from './RecipeTileBody'
import { RecipeTileCustomisationDropdown } from './RecipeTileCustomisationDropdown'
import { RecipeTileImageSection } from './RecipeTileImageSection'
import { Card, RecipeDetailsOverlayButton } from './RecipeTile.styles'
import type { RecipeTileProps } from './RecipeTile.types'
import { getRecipeTileAriaLabel } from './RecipeTile.utils'

export const RecipeTile = ({
  recipe,
  onClick,
  dataTestingId = recipe.id,
  lazy = true,
  insideOverflow = false,
  isInBasket,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  quantity,
  customisationData,
  onCustomiseOpen,
  onCustomiseClose,
  onCustomisationSelect,
  onCustomisationRetry,
  showCustomisationEducation = false,
  onCustomisationEducationClose,
  renderFlexPortionBadge,
  renderFlexPortionEditor,
}: RecipeTileProps) => {
  const recipeTileRef = useRef<HTMLDivElement>(null)
  const isDesktop = useDeviceType() === DeviceType.DESKTOP
  const isOutOfStock = !recipe.isAvailable && !isInBasket
  const isCustomisationOpen = Boolean(customisationData?.hasOpenedCustomisations)
  const isCustomisationSelectionPending = Boolean(
    customisationData?.isCustomisationSelectionPending,
  )
  const handleDetailsClick = () => {
    onCustomiseClose?.()
    onClick(recipe.id)
  }
  const handleCustomiseClick = onCustomiseOpen
    ? () => {
        if (isCustomisationOpen) {
          onCustomiseClose?.()
          return
        }

        if (showCustomisationEducation) {
          onCustomisationEducationClose?.()
        }

        onCustomiseOpen(recipe.id)
      }
    : undefined
  const detailsAriaLabel = getRecipeTileAriaLabel(recipe, { isOutOfStock })
  const flexPortionBadge = renderFlexPortionBadge?.(recipe.id)
  const flexPortionEditor = renderFlexPortionEditor?.(recipe.id)
  const showFlexPortions = Boolean(flexPortionBadge || flexPortionEditor)

  useEffect(() => {
    if (!isDesktop || !isCustomisationOpen || !onCustomiseClose) {
      return undefined
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (recipeTileRef.current && !recipeTileRef.current.contains(event.target as Node)) {
        onCustomiseClose()
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)

    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isCustomisationOpen, isDesktop, onCustomiseClose])

  return (
    <Card
      ref={recipeTileRef}
      data-testid="recipe-tile"
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={dataTestingId}
      data-customisations-loading={customisationData?.isLoadingCustomisations}
      data-customisations-opened={customisationData?.hasOpenedCustomisations}
      data-customisations-error={customisationData?.hasCustomisationError}
      bg={Color.White}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
    >
      <RecipeDetailsOverlayButton
        data-testid="recipe-tile-details-button"
        type="button"
        aria-label={detailsAriaLabel}
        onClick={handleDetailsClick}
      />
      <RecipeTileImageSection
        recipe={recipe}
        isOutOfStock={isOutOfStock}
        lazy={lazy}
        insideOverflow={insideOverflow}
        flexPortionBadge={flexPortionBadge}
        isFlexPortionEditorOpen={Boolean(flexPortionEditor)}
      />
      <RecipeTileBody
        recipe={recipe}
        isOutOfStock={isOutOfStock}
        isInBasket={isInBasket}
        canAddRecipes={canAddRecipes}
        reachedLimit={reachedLimit}
        onAddClick={onAddClick}
        onRemoveClick={onRemoveClick}
        onBlockedAddAttempt={onBlockedAddAttempt}
        isCustomisationOpen={isCustomisationOpen}
        isBasketButtonDisabled={isCustomisationSelectionPending}
        onCustomiseClick={handleCustomiseClick}
        showFlexPortions={showFlexPortions}
      />
      <RecipeBanner recipe={recipe} isOutOfStock={isOutOfStock} />
      {isCustomisationOpen && (
        <RecipeTileCustomisationDropdown
          recipe={recipe}
          isInBasket={isInBasket}
          quantity={quantity}
          canAddRecipes={canAddRecipes}
          reachedLimit={reachedLimit}
          onAddClick={onAddClick}
          onRemoveClick={onRemoveClick}
          onBlockedAddAttempt={onBlockedAddAttempt}
          customisationData={customisationData}
          onClose={onCustomiseClose}
          onSelectCustomisation={onCustomisationSelect}
          onRetry={onCustomisationRetry}
        />
      )}
      {showCustomisationEducation && onCustomisationEducationClose && (
        <Box
          left="50%"
          position={DisplayPosition.Absolute}
          top="100%"
          width="100%"
          zIndex={ZIndex.Mask}
          style={{ marginTop: '-2px', transform: 'translateX(-50%)' }}
        >
          <CustomisationEducation onClose={onCustomisationEducationClose} />
        </Box>
      )}
      {flexPortionEditor}
    </Card>
  )
}
